/**
 * TaxSetu AI - Terms of Service Page
 * ====================================
 * Required for Razorpay verification
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Scale, FileText } from 'lucide-react';

export default function TermsPage() {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen bg-[#0a1628]">
      {/* Header */}
      <header className="bg-[#0f2744] border-b border-[#1e3a5f] px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <button 
            onClick={() => navigate(-1)}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-xl font-bold text-white">
            Tax<span className="text-[#38bdf8]">Setu</span> AI
          </h1>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="flex items-center gap-3 mb-8">
          <FileText className="w-8 h-8 text-[#38bdf8]" />
          <h1 className="text-3xl font-bold text-white">Terms of Service</h1>
        </div>
        
        <div className="bg-[#0f2744] rounded-xl p-8 border border-[#1e3a5f] prose prose-invert max-w-none">
          <p className="text-gray-400 text-sm mb-6">Last Updated: January 19, 2026</p>
          
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">1. Acceptance of Terms</h2>
            <p className="text-gray-300 leading-relaxed">
              By accessing and using TaxSetu AI ("Service"), operated by Credence Tax Advisors (OPC) Private Limited ("Company", "we", "us"), 
              you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our Service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">2. Description of Service</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              TaxSetu AI is an AI-powered tax assistance platform that provides:
            </p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li>AI-based tax query responses and guidance</li>
              <li>Income Tax Act 1961 and 2025 section mapping</li>
              <li>Tax calculation tools and estimators</li>
              <li>Notice analysis assistance</li>
              <li>Educational content on Indian taxation</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">3. Disclaimer</h2>
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 mb-4">
              <p className="text-yellow-400 font-medium">Important Notice</p>
            </div>
            <p className="text-gray-300 leading-relaxed">
              TaxSetu AI provides information for educational purposes only. The AI-generated responses 
              should NOT be considered as professional tax advice, legal advice, or a substitute for 
              consultation with a qualified Chartered Accountant or Tax Professional. Always verify 
              information with official sources and consult professionals for specific tax matters.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">4. User Accounts</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              To access certain features, you may need to create an account using Google Sign-In. You agree to:
            </p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li>Provide accurate and complete information</li>
              <li>Maintain the security of your account credentials</li>
              <li>Accept responsibility for all activities under your account</li>
              <li>Notify us immediately of any unauthorized access</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">5. Subscription Plans</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              We offer the following subscription tiers:
            </p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li><strong>Free Plan:</strong> Limited daily queries with basic features</li>
              <li><strong>Pro Plan:</strong> Unlimited queries with premium features including advanced calculators and priority support</li>
            </ul>
            <p className="text-gray-300 leading-relaxed mt-4">
              Subscription fees are charged in advance on a monthly or annual basis as selected. 
              All fees are in Indian Rupees (INR) and inclusive of applicable taxes.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">6. Payment Terms</h2>
            <p className="text-gray-300 leading-relaxed">
              Payments are processed securely through Razorpay. We accept UPI, credit/debit cards, 
              and net banking. By making a payment, you authorize us to charge the applicable fees 
              to your chosen payment method.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">7. Intellectual Property</h2>
            <p className="text-gray-300 leading-relaxed">
              All content, features, and functionality of TaxSetu AI, including but not limited to 
              text, graphics, logos, and software, are owned by Credence Tax Advisors (OPC) Private Limited and are protected 
              by intellectual property laws.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">8. Prohibited Activities</h2>
            <p className="text-gray-300 leading-relaxed mb-4">You agree not to:</p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li>Use the Service for any illegal purpose</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Interfere with or disrupt the Service</li>
              <li>Resell or redistribute the Service without permission</li>
              <li>Use automated systems to access the Service excessively</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">9. Limitation of Liability</h2>
            <p className="text-gray-300 leading-relaxed">
              To the maximum extent permitted by law, Credence Tax Advisors (OPC) Private Limited shall not be liable for 
              any indirect, incidental, special, consequential, or punitive damages arising from 
              your use of the Service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">10. Governing Law</h2>
            <p className="text-gray-300 leading-relaxed">
              These Terms shall be governed by and construed in accordance with the laws of India. 
              Any disputes shall be subject to the exclusive jurisdiction of the courts in Delhi, India.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">11. Contact Information</h2>
            <p className="text-gray-300 leading-relaxed">
              For questions about these Terms, please contact us at:
            </p>
            <div className="mt-4 p-4 bg-[#1a3654] rounded-lg">
              <p className="text-white font-medium">Credence Tax Advisors (OPC) Pvt. Ltd.</p>
              <p className="text-gray-400">Email: support@taxsetu.ai</p>
              <p className="text-gray-400">Phone: +91 9711162342</p>
              <p className="text-gray-400">Address: A-50, Street-3, New Modern Shahdara, Delhi-110032</p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
